/**
 * Phase 9 — V2 landing bilingual state.
 * Reads/normalizes `?lang=`, exposes { lang, setLang, buildHref }.
 * Page owns the language; children receive resolved copy + buildHref.
 */
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function normalizeV2Language(v) {
  if (typeof v === 'string' && v.trim().toLowerCase() === 'es') return 'es';
  return 'en';
}

export default function useV2Language() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const lang = normalizeV2Language(params.get('lang'));

  const setLang = useCallback((next) => {
    const norm = normalizeV2Language(next);
    if (norm === lang) return;
    const p = new URLSearchParams(location.search);
    p.set('lang', norm);
    navigate(`${location.pathname}?${p.toString()}${location.hash || ''}`, { replace: false });
  }, [lang, location.hash, location.pathname, location.search, navigate]);

  const buildHref = useCallback((target) => {
    if (typeof target !== 'string' || !target) return target;
    // External / non-V2 targets → unchanged
    if (/^(mailto:|tel:|https?:|#)/i.test(target)) return target;
    // Internal V2 targets: /v2, /v2/*, and the public landing root '/'
    const isInternal = target === '/' || target.startsWith('/v2');
    if (!isInternal) return target;
    // Parse path + query + hash
    const hashIdx = target.indexOf('#');
    const hash = hashIdx >= 0 ? target.slice(hashIdx) : '';
    const noHash = hashIdx >= 0 ? target.slice(0, hashIdx) : target;
    const qIdx = noHash.indexOf('?');
    const path = qIdx >= 0 ? noHash.slice(0, qIdx) : noHash;
    const qs = qIdx >= 0 ? noHash.slice(qIdx + 1) : '';
    const p = new URLSearchParams(qs);
    p.set('lang', lang);
    return `${path}?${p.toString()}${hash}`;
  }, [lang]);

  return { lang, setLang, buildHref };
}
